import { useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import { TbDotsVertical, TbListDetails, TbPlayerPlay, TbPlus, TbWebhook } from "react-icons/tb";

const STATUS_STYLES = {
  success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  failed: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const WebhookActionsMenu = ({ item, onTest, onViewLogs, testingId, manageDisabled = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const testing = testingId === item.id;

  return (
    <div ref={rootRef} className="relative flex justify-start lg:justify-end">
      <button
        type="button"
        className="btn-outline-icon"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.get("baas.api.webhooks.actions")}
      >
        <TbDotsVertical className="h-4 w-4" />
      </button>

          {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[10.5rem] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-card dark:border-brand-dark-border dark:bg-brand-dark-elevated"
        >
          {!manageDisabled ? (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-dark hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-100 dark:hover:bg-brand-dark-border/40"
              onClick={() => {
                setOpen(false);
                onTest(item.id);
              }}
              disabled={testing}
            >
              <TbPlayerPlay className="h-4 w-4 shrink-0" />
              {testing
                ? intl.get("baas.api.webhooks.testing")
                : intl.get("baas.api.webhooks.test")}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-brand-dark-border/40"
            onClick={() => {
              setOpen(false);
              onViewLogs(item);
            }}
          >
            <TbListDetails className="h-4 w-4 shrink-0" />
            {intl.get("baas.api.webhooks.viewLogs")}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const WebhookRow = ({
  item,
  onToggle,
  onTest,
  onViewLogs,
  testingId,
  manageDisabled = false,
}) => (
  <div className="grid grid-cols-1 gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 dark:border-brand-dark-border lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_auto] lg:items-center">
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
        <TbWebhook className="h-4 w-4 text-brand-dark dark:text-gray-100" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">{item.name}</p>
        <p className="truncate text-[11px] text-gray-400">
          {item.events?.length
            ? intl.get("baas.api.webhooks.eventsCount", { count: item.events.length })
            : "—"}
        </p>
      </div>
    </div>

    <div className="min-w-0">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.webhooks.urlLabel")}
      </p>
      <p className="truncate text-sm text-gray-600 dark:text-gray-300">{item.url}</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {item.events.map((eventName) => (
          <span
            key={eventName}
            className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300"
          >
            {eventName}
          </span>
        ))}
      </div>
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.webhooks.lastDelivery")}
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-300">{formatDate(item.lastDeliveryAt)}</p>
      {item.lastStatus ? (
        <span
          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            STATUS_STYLES[item.lastStatus] ?? STATUS_STYLES.failed
          }`}
        >
          {intl.get(`baas.api.webhooks.deliveryStatus.${item.lastStatus}`)}
        </span>
      ) : null}
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.webhooks.activeLabel")}
      </p>
      <label
        className={`inline-flex items-center gap-2 text-sm text-brand-dark dark:text-gray-100 ${
          manageDisabled ? "cursor-default opacity-80" : "cursor-pointer"
        }`}
      >
        <input
          type="checkbox"
          checked={item.active}
          onChange={(event) => onToggle(item.id, event.target.checked)}
          disabled={manageDisabled}
          className="accent-[#e7a73f]"
        />
        {item.active
          ? intl.get("baas.api.webhooks.active")
          : intl.get("baas.api.webhooks.inactive")}
      </label>
    </div>

    <WebhookActionsMenu
      item={item}
      onTest={onTest}
      onViewLogs={onViewLogs}
      testingId={testingId}
      manageDisabled={manageDisabled}
    />
  </div>
);

export const BaasWebhooksPanel = ({
  webhooks,
  onRegister,
  onToggle,
  onTest,
  onViewLogs,
  testingId,
  manageDisabled = false,
  emptyMessage,
}) => (
  <div className="min-w-0">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.api.webhooks.sectionTitle")}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.api.webhooks.sectionSubtitle")}
        </p>
      </div>
      {!manageDisabled ? (
        <button
          type="button"
          className="btn-primary shrink-0"
          onClick={onRegister}
        >
          <TbPlus className="h-4 w-4" />
          {intl.get("baas.api.webhooks.register")}
        </button>
      ) : null}
    </div>

    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_auto]">
        <span>{intl.get("baas.api.webhooks.nameLabel")}</span>
        <span>{intl.get("baas.api.webhooks.urlLabel")}</span>
        <span>{intl.get("baas.api.webhooks.lastDelivery")}</span>
        <span>{intl.get("baas.api.webhooks.activeLabel")}</span>
        <span className="text-right">{intl.get("baas.api.webhooks.actions")}</span>
      </div>

      {webhooks.length ? (
        webhooks.map((item) => (
          <WebhookRow
            key={item.id}
            item={item}
            onToggle={onToggle}
            onTest={onTest}
            onViewLogs={onViewLogs}
            testingId={testingId}
            manageDisabled={manageDisabled}
          />
        ))
      ) : (
        <div className="app-empty-state p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage || intl.get("baas.api.webhooks.empty")}
        </div>
      )}
    </div>
  </div>
);
